import React from "react";

import {DayPicker} from "react-day-picker";

import {WorkoutContext, useWorkout} from "@client/features/workouts";

import * as dates from "@client/dates";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {DateCarousel} from "@client/components/DateCarousel";
import {DaySummary} from "@client/components/DaySummary";
import {Survey} from "@client/components/JournalWorkout";
import {MealCard} from "@client/components/MealCard";
import {PieChart} from "@client/components/PieChart";
import {ReadOnlyDaySummary} from "@client/components/ReadOnlyDaySummary";
// import {Habits} from "@client/scenes/Journal";
import {Workout} from "@client/components/Workout";
import {ManagerLayout, ManagerTabs} from "@client/site/ManagerLayout";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {reverse, server} from "@reactivated";

export const Template = (props: server.manager.templates.ClientDayDetail) => {
    // const [journal, setJournal] = React.useState(props.journal);
    const day = props.day;

    const [isDayPickerOpen, setIsDayPickerOpen] = React.useState(false);
    const date = dates.parseISO(day.key);
    const next = dates.formatISO(dates.addDays(date, 1), {representation: "date"});
    const previous = dates.formatISO(dates.subDays(date, 1), {
        representation: "date",
    });

    const {request} = useSiteContext();
    const params = new URL(request.url).searchParams;

    const title = utils.formatDate(day.key);
    const handleDateClick = (pickedDate: Date) => {
        window.location.href =
            reverse("client_days", {
                client_manager_id: props.client_manager.id,
                date: dates.formatISO(pickedDate, {representation: "date"}),
            }) + `?${params.toString()}`;
        setIsDayPickerOpen(false);
    };

    const handlers = useWorkout({
        workout: props.journal.workout ?? {
            sections: [],
            questions: [],
        },
        exercise_maxes: props.journal.exercise_maxes,
        /* Prevent editing when options are changed, so as to not give the impression editing is working. */
        setWorkout: () => {},
    });

    const renderedJournal = (
        <form
            noValidate
            method="POST"
            action=""
            className="
                flex flex-col gap-4 pt-4
                lg:pb-64
            "
        >
            <forms.CSRFToken />
            <div className="m-[-10px] space-y-2">
                {/*
                <Habits journal={journal} setJournal={setJournal} profile={props.profile} />
                */}
                {day.meals
                    .filter((meal) => meal.name != "__SUPPLEMENTS__")
                    .map((meal) => {
                        return (
                            <div key={meal.uuid}>
                                <MealCard
                                    meal={meal}
                                    hiddenMacros={false}
                                    defaultExpanded={true}
                                />
                            </div>
                        );
                    })}
            </div>
            <div
                className="
                    border-gray-200
                    lg:fixed lg:inset-x-0 lg:bottom-0 lg:border-t lg:bg-white lg:pb-10
                    lg:pl-60
                "
            >
                <DaySummary useKilojoules={false} day={day} target={day.target} />
                <div
                    className="
                        mt-4 h-[120px] max-w-[400px]
                        md:mx-auto md:mt-[10px] md:mb-[-30px]
                    "
                >
                    <PieChart {...props.status} />
                </div>
            </div>
            {props.client_manager.client.is_reports_enabled && (
                <>
                    <typography.Subheader>Measurements</typography.Subheader>
                    <forms.Fields form={props.measurement_form} />
                </>
            )}
            <typography.Subheader>Notes</typography.Subheader>
            <forms.Fields form={props.comment_form} />
            <typography.Button submit>Submit</typography.Button>
        </form>
    );

    return (
        <ManagerLayout
            narrow
            title={title}
            currentClientManagerId={props.client_manager.id}
        >
            <div className="relative">
                <div className="flex justify-center">
                    <DateCarousel
                        previousDayLink={
                            reverse("client_days", {
                                client_manager_id: props.client_manager.id,
                                date: previous,
                            }) + `?${params.toString()}`
                        }
                        nextDayLink={
                            reverse("client_days", {
                                client_manager_id: props.client_manager.id,
                                date: next,
                            }) + `?${params.toString()}`
                        }
                        onDateClick={() => setIsDayPickerOpen(!isDayPickerOpen)}
                        onPreviousClick={() => {
                            /* No op, only used in the app to reverse animations */
                        }}
                        date={day.key}
                    />
                </div>
                <div>
                    {isDayPickerOpen === true && (
                        <DayPicker
                            className="
                                absolute left-1/2 z-50 mt-[3px] -translate-x-1/2 border
                                border-[#D8D8D8] bg-white p-2.5
                            "
                            defaultMonth={date}
                            selected={date}
                            onDayClick={handleDateClick}
                        />
                    )}
                </div>
            </div>
            <ManagerTabs
                tabs={[
                    {
                        name: "journal",
                        title: "Journal",
                        content: renderedJournal,
                        icon: "custom-0283-contacts",
                    },
                    {
                        name: "workout",
                        title: "Workout",
                        content: (
                            <>
                                {props.journal.workout == null ? (
                                    <div>No workout for this day</div>
                                ) : (
                                    <WorkoutContext.Provider
                                        value={{
                                            workout: props.journal.workout,
                                            scope: "manager",
                                            startRestTimer: () => {},
                                        }}
                                    >
                                        <Workout
                                            workout_uuid={props.journal.workout.uuid}
                                            handlers={handlers}
                                            isTemplate={false}
                                            readOnly
                                        />
                                        <div>
                                            <Survey
                                                questions={handlers.questions}
                                                readOnly={true}
                                                setQuestions={handlers.setQuestions}
                                            />
                                        </div>
                                    </WorkoutContext.Provider>
                                )}
                            </>
                        ),
                        icon: "custom-dumbbell",
                    },
                    {
                        name: "summary",
                        title: "Summary",
                        content: (
                            <ReadOnlyDaySummary
                                day={props.day}
                                target={props.day.target}
                                targets={props.journal.targets}
                                useKilojoules={props.profile.use_kilojoules}
                                profile={props.profile}
                            />
                        ),
                        icon: "0508-chart-growth",
                    },
                ]}
            />
        </ManagerLayout>
    );
};
